import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Plus, Trash2, Save, Lock, Info, Loader2 } from 'lucide-react';
import type { EffortEntry } from '@shared/schema';

interface EffortEntryRow {
  id: string;
  title: string;
  description: string;
  estimatedHours: string;
}

interface EffortEntriesEditorProps {
  pulseId: number;
  committedAt: string | null;
}

function createEmptyRow(): EffortEntryRow {
  return {
    id: crypto.randomUUID(),
    title: '',
    description: '',
    estimatedHours: '',
  };
}

export function EffortEntriesEditor({ pulseId, committedAt }: EffortEntriesEditorProps) {
  const [rows, setRows] = useState<EffortEntryRow[]>([createEmptyRow()]);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isCommitted = !!committedAt;

  const { data: entries, isLoading } = useQuery<EffortEntry[]>({
    queryKey: ['/api/pulses', pulseId, 'effort-entries'],
    queryFn: async () => {
      const res = await fetch(`/api/pulses/${pulseId}/effort-entries`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch effort entries');
      return res.json();
    },
    enabled: !!pulseId,
  });

  useEffect(() => {
    setHasLoaded(false);
  }, [pulseId]);

  useEffect(() => {
    if (entries && !hasLoaded) {
      if (entries.length > 0) {
        setRows(entries.map(e => ({
          id: e.id,
          title: e.title,
          description: e.description || '',
          estimatedHours: e.estimatedHours != null ? String(Number(e.estimatedHours)) : '',
        })));
      } else {
        setRows([createEmptyRow()]);
      }
      setHasLoaded(true);
    }
  }, [entries, hasLoaded]);

  const setRef = useCallback((key: string, el: HTMLInputElement | null) => {
    if (el) {
      inputRefs.current.set(key, el);
    } else {
      inputRefs.current.delete(key);
    }
  }, []);

  const addRow = useCallback(() => {
    const newRow = createEmptyRow();
    setRows(prev => [...prev, newRow]);
    setTimeout(() => {
      const el = inputRefs.current.get(`title-${newRow.id}`);
      el?.focus();
    }, 50);
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter(r => r.id !== id);
    });
  }, []);

  const updateRow = useCallback((id: string, field: keyof EffortEntryRow, value: string) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
  }, []);

  const handleTabOnLastCell = useCallback((e: React.KeyboardEvent, rowIndex: number) => {
    if (e.key === 'Tab' && !e.shiftKey && rowIndex === rows.length - 1) {
      e.preventDefault();
      addRow();
    }
  }, [rows.length, addRow]);

  const getPayload = useCallback(() => {
    return rows
      .filter(r => r.title.trim() || r.description.trim() || r.estimatedHours.trim())
      .map((r, index) => ({
        id: r.id,
        title: r.title.trim(),
        description: r.description.trim() || null,
        estimatedHours: parseFloat(r.estimatedHours || '0').toFixed(2),
        sortOrder: index,
      }));
  }, [rows]);

  const saveDraftMutation = useMutation({
    mutationFn: async () => {
      const payload = getPayload();
      const res = await apiRequest('PUT', `/api/pulses/${pulseId}/effort-entries`, { entries: payload });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pulses', pulseId, 'effort-entries'] });
      toast({ title: 'Draft saved' });
    },
    onError: (error: any) => {
      toast({ title: 'Error while saving', description: error.message, variant: 'destructive' });
    },
  });

  const commitMutation = useMutation({
    mutationFn: async () => {
      const payload = getPayload();
      const res = await apiRequest('POST', `/api/pulses/${pulseId}/effort-commit`, { entries: payload });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pulses', pulseId, 'effort-entries'] });
      queryClient.invalidateQueries({ queryKey: ['pulse-workbench', pulseId] });
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
      toast({ title: 'Effort estimate committed', description: 'The data was submitted to Finance.' });
    },
    onError: (error: any) => {
      toast({ title: 'Error while committing', description: error.message, variant: 'destructive' });
    },
  });

  const { totalHours, days, remainingHours } = useMemo(() => {
    const total = rows.reduce((sum, r) => {
      const h = parseFloat(r.estimatedHours || '0');
      return sum + (isNaN(h) ? 0 : h);
    }, 0);
    const d = Math.floor(total / 8);
    const rem = total % 8;
    return { totalHours: total, days: d, remainingHours: parseFloat(rem.toFixed(2)) };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading effort estimate...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="effort-entries-editor">
      {isCommitted && (
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-900/30 border border-emerald-700/50 rounded-md">
          <Lock className="h-4 w-4 text-emerald-400 flex-shrink-0" />
          <span className="text-sm text-emerald-300">
            Festgeschrieben am{' '}
            {new Date(committedAt!).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            {' um '}
            {new Date(committedAt!).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      )}

      <div className="border border-slate-700 rounded-md overflow-hidden">
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_100px_40px] gap-0 bg-slate-800/80 border-b border-slate-700">
          <div className="px-3 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider">Title</div>
          <div className="px-3 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider">Description</div>
          <div className="px-3 py-2 text-xs font-medium text-slate-400 uppercase tracking-wider text-right">Hours</div>
          <div className="px-3 py-2" />
        </div>

        <div className="divide-y divide-slate-700/50">
          {rows.map((row, index) => (
            <div
              key={row.id}
              className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_100px_40px] gap-0 group hover:bg-slate-800/30"
              data-testid={`effort-entry-row-${index}`}
            >
              <div className="px-1.5 py-1">
                {isCommitted ? (
                  <div className="px-2 py-1.5 text-sm text-slate-200">{row.title}</div>
                ) : (
                  <Input
                    ref={(el) => setRef(`title-${row.id}`, el)}
                    value={row.title}
                    onChange={(e) => updateRow(row.id, 'title', e.target.value)}
                    placeholder="Line item..."
                    className="h-8 bg-transparent border-0 text-sm text-slate-200 placeholder-slate-500 focus-visible:ring-1 focus-visible:ring-blue-500 rounded-sm"
                    data-testid={`input-effort-title-${index}`}
                  />
                )}
              </div>
              <div className="px-1.5 py-1">
                {isCommitted ? (
                  <div className="px-2 py-1.5 text-sm text-slate-300">{row.description || '—'}</div>
                ) : (
                  <Input
                    value={row.description}
                    onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                    placeholder="Description..."
                    className="h-8 bg-transparent border-0 text-sm text-slate-300 placeholder-slate-500 focus-visible:ring-1 focus-visible:ring-blue-500 rounded-sm"
                    data-testid={`input-effort-desc-${index}`}
                  />
                )}
              </div>
              <div className="px-1.5 py-1">
                {isCommitted ? (
                  <div className="px-2 py-1.5 text-sm text-slate-200 text-right font-mono">
                    {row.estimatedHours ? parseFloat(row.estimatedHours).toFixed(2) : '0.00'}
                  </div>
                ) : (
                  <Input
                    value={row.estimatedHours}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, '');
                      updateRow(row.id, 'estimatedHours', val);
                    }}
                    onKeyDown={(e) => handleTabOnLastCell(e, index)}
                    placeholder="0.00"
                    className="h-8 bg-transparent border-0 text-sm text-slate-200 placeholder-slate-500 text-right font-mono focus-visible:ring-1 focus-visible:ring-blue-500 rounded-sm"
                    data-testid={`input-effort-hours-${index}`}
                  />
                )}
              </div>
              <div className="px-1 py-1 flex items-center justify-center">
                {!isCommitted && rows.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRow(row.id)}
                    className="h-7 w-7 p-0 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`button-remove-effort-${index}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {!isCommitted && (
        <Button
          variant="ghost"
          size="sm"
          onClick={addRow}
          className="text-blue-400 hover:text-blue-300 text-xs"
          data-testid="button-add-effort-row"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add line item
        </Button>
      )}

      <div className="flex items-center justify-between px-3 py-3 bg-slate-800/60 border border-slate-700 rounded-md">
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">Total:</span>
          <span className="text-lg font-semibold text-white font-mono">
            {totalHours.toFixed(2)} h
          </span>
          <span className="text-sm text-slate-400">
            = {days} {days === 1 ? 'day' : 'days'}{remainingHours > 0 ? `, ${remainingHours.toFixed(2)} h` : ''}
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-slate-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-slate-800 border-slate-600 text-slate-200 text-xs">
                1 working day = 8 hours
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {!isCommitted && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => saveDraftMutation.mutate()}
              disabled={saveDraftMutation.isPending || commitMutation.isPending}
              className="text-slate-300 border-slate-600 hover:bg-slate-700"
              data-testid="button-save-effort-draft"
            >
              {saveDraftMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5 mr-1.5" />
              )}
              Save draft
            </Button>
            <Button
              size="sm"
              onClick={() => setShowCommitDialog(true)}
              disabled={saveDraftMutation.isPending || commitMutation.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="button-commit-effort"
            >
              {commitMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Lock className="h-3.5 w-3.5 mr-1.5" />
              )}
              Commit & Sync
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={showCommitDialog} onOpenChange={setShowCommitDialog}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Commit effort estimate?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              The estimate will be locked and submitted to the Finance app. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => commitMutation.mutate()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Commit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
